import { Fragment, type FormEvent } from "react";
import { NavLink } from "react-router-dom-v5-compat";
import {
  IconNameEnum,
  useFlightStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { FLIGHTS_PATH, useDeviceType } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { MAX_MULTICITY_ROUTES } from "@hopper-b2b/types";
import { TripCategory } from "@overrides/types";
import {
  CalendarPicker,
  FlightCategorySelection,
  FlightPassengerSelection,
  useFlightSearch,
} from "@tenant/flights";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import { FlightSearchRow } from "./components/FlightSearchRow";
import defaultStyles from "./FlightSearch.module.scss";

export interface FlightSearchComponentProps extends PlatformComponentProps {
  onSearch?: () => void;
  hidePassengerSelection?: boolean;
  hideTripCategorySelection?: boolean;
  showSearchButton?: boolean;
}

export const FlightSearch = (props: FlightSearchComponentProps) => {
  const {
    className,
    children,
    hidePassengerSelection,
    hideTripCategorySelection,
    onSearch: onSearchProp,
    showSearchButton,
  } = props;

  const COMPONENT_KEY = CoreFlightsComponent.FlightSearch;
  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { context, handlers } = useFlightSearch();

  const { t } = useI18nContext();
  const { isDesktopAndUp } = useDeviceType();
  const showMobileHeader = !isDesktopAndUp;

  const onSubmitForm = (e?: FormEvent) => {
    e?.preventDefault();

    // Call the callback only if form is valid
    if (onSearchProp != null && context.validSearchParams != null) {
      onSearchProp();
    }

    // This will validate and navigate if valid
    handlers.onSubmitForm();
  };

  const onCalendarSearch = () => {
    if (context.validSearchParams != null) {
      handlers.onSearch(context.validSearchParams, context.tripCategory);
    }
  };

  return (
    <div className={clsx(block, className)}>
      {showMobileHeader && (
        <div className={cn("mobile-header")}>
          <h1 className={cn("mobile-header-title")}>
            {t("core-flights.search.mobileSearch.title")}
          </h1>
          <NavLink
            className={cn("mobile-header-close")}
            to={FLIGHTS_PATH}
            aria-label={t("close.button")}
          >
            <Icon iconName={IconNameEnum.close} />
          </NavLink>
        </div>
      )}
      <form className={clsx(cn("form"))} onSubmit={onSubmitForm}>
        <div className={cn("traveler-category-row")}>
          {hidePassengerSelection ? null : (
            <div
              className={clsx(
                cn("traveler-selection-container"),
                cn("cell-content", ["withBorder"])
              )}
            >
              <FlightPassengerSelection
                updatePaxCount={handlers.onPassengerCountChange}
                paxCount={context.passengerCounts}
              />
            </div>
          )}
          {hideTripCategorySelection ? null : (
            <div className={cn("category-selection-container")}>
              <FlightCategorySelection
                tripCategoryChangeHandler={handlers.onTripCategoryChange}
                category={context.tripCategory}
              />
            </div>
          )}
        </div>
        {context.tripCategory !== TripCategory.MULTI_CITY && (
          <div className={cn("search-row")}>
            <FlightSearchRow
              onOriginChange={handlers.onOriginChange}
              onDestinationChange={handlers.onDestinationChange}
              onOriginFocus={handlers.onOriginFocus}
              onDestinationFocus={handlers.onDestinationFocus}
              selectedOrigin={context.origin}
              selectedDestination={context.destination}
              errors={context.formError}
              onSwap={handlers.onSwapOriginDestination}
            />
            <div
              className={clsx(
                cn("search-button-container", [context.tripCategory], {
                  show: showSearchButton,
                })
              )}
            >
              <button type="submit" className={cn("search-button")}>
                {t("flightsPageTitles.searchFlights")}
              </button>
            </div>
          </div>
        )}
        {context.tripCategory === TripCategory.MULTI_CITY && (
          <div className={cn("multicity-routes-container")}>
            {context.multicityRoutes.map((route, index) => {
              return (
                <Fragment key={index}>
                  <p className={cn("multicity-route-title")}>
                    <Icon iconName={IconNameEnum.airplane} />
                    {t("core-flights.multicity.search.label", {
                      index: index + 1,
                    })}
                  </p>
                  <FlightSearchRow
                    onOriginChange={(origin) =>
                      handlers.onMulticityOriginChange(origin, index)
                    }
                    onDestinationChange={(destination) =>
                      handlers.onMulticityDestinationChange(destination, index)
                    }
                    onOriginFocus={() => handlers.onMulticityOriginFocus(index)}
                    onDestinationFocus={() =>
                      handlers.onMulticityDestinationFocus(index)
                    }
                    selectedOrigin={route.origin}
                    selectedDestination={route.destination}
                    errors={{
                      origin:
                        context.formError?.multicityRoutes?.[index]?.origin,
                      destination:
                        context.formError?.multicityRoutes?.[index]
                          ?.destination,
                      dates: context.formError?.multicityRoutes?.[index]?.dates,
                    }}
                    index={index}
                    isMulticity={true}
                  />
                  {index > 1 && (
                    <button
                      type="button"
                      className={cn("remove-multicity-route-button")}
                      onClick={() => {
                        handlers.onRemoveMulticityRoute(index);
                      }}
                    >
                      <Icon iconName={IconNameEnum.close} />
                      <span
                        className={cn("remove-multicity-route-button-text")}
                      >
                        {t("core-flights.multicity.search.removeFlight")}
                      </span>
                    </button>
                  )}
                  <div className={cn("divider")} />
                </Fragment>
              );
            })}

            <div className={cn("multicity-actions-row")}>
              <div
                className={clsx(
                  cn("search-button-container", [context.tripCategory], {
                    show: showSearchButton,
                  })
                )}
              >
                <button type="submit" className={cn("search-button")}>
                  {t("flightsPageTitles.searchFlights")}
                </button>
              </div>
              <button
                type="button"
                className={cn("add-multicity-route-button")}
                onClick={() => {
                  handlers.onAddMulticityRoute();
                }}
                disabled={
                  context.multicityRoutes.length >= MAX_MULTICITY_ROUTES
                }
              >
                {t("core-flights.search.addFlight")}
              </button>
            </div>
            {context.multicityRoutes.length >= MAX_MULTICITY_ROUTES && (
              <span className={cn("multicity-error-text")}>
                {t("core-flights.search.multicityError")}
              </span>
            )}
          </div>
        )}

        {children}
      </form>
      <div
        className={clsx(
          cn("continue-button-container", [context.tripCategory])
        )}
      >
        <button
          onClick={() => {
            handlers.onNavigateToCalendar();
          }}
          disabled={!context.isSearchCompleteWithoutDates}
          className={cn("continue-button")}
        >
          {t("core-flights.search.dateSelection.chooseDates")}
        </button>
      </div>
      <CalendarPicker
        open={context.openCalendar}
        onSearch={onCalendarSearch}
        onClose={handlers.onCalendarClose}
        showApplyButton={context.tripCategory === TripCategory.MULTI_CITY}
        preventSameDateSelection={false}
      />
    </div>
  );
};
